<script lang="ts" setup>
import { ref, computed, watch } from 'vue'
import {
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuRoot,
  DropdownMenuTrigger,
} from 'radix-vue'
import Icon from '@/components/shared/Icon.vue'
import Button from '@/components/shared/Button.vue'
import Checkbox from '@/components/ui/Checkbox.vue'
import Loading from '@/components/shared/Loading.vue'
import { useSearch, SELECT_ALL_SILO_LIMIT } from '@/composables/useSearch'
import FieldText from '@/components/shared/Fields/FieldText.vue'
import { useForm } from 'vee-validate'

const props = defineProps<{
  selectedSilos: string[]
  applicationsOnly?: boolean
  resetToClear?: boolean
  contentZClass?: string
  portalDisabled?: boolean
  portalTarget?: string | HTMLElement
}>()

const emit = defineEmits<{
  updateSilos: [silos: string[]]
}>()

const dropdownOpen = ref(false)
const localSelectedSilos = ref<Set<string>>(
  props.selectedSilos.length > 0 ? new Set(props.selectedSilos) : new Set(),
)
const scrollContainerRef = ref<HTMLElement | null>(null)
const contentRef = ref<HTMLElement | null>(null)

const { values, setFieldValue } = useForm<{ siloSearch: string }>({
  initialValues: {
    siloSearch: '',
  },
})

const searchTerm = computed(() => values.siloSearch || '')

// Use unified search composable for silo data
const { availableSilos, silosLoading } = useSearch()

const selectableSilos = computed(() =>
  props.applicationsOnly
    ? availableSilos.value.filter((silo) => silo.isResource)
    : availableSilos.value,
)

const canSelectAll = computed(() => {
  const count = selectableSilos.value.length
  return count > 0 && count <= SELECT_ALL_SILO_LIMIT
})

// Sync local state with props when they change
watch(
  () => props.selectedSilos,
  (newSilos) => {
    localSelectedSilos.value = new Set(newSilos)
  },
  { deep: true },
)

// Filter silos based on search term
const filteredSilos = computed(() => {
  const term = searchTerm.value.toLowerCase().trim()
  if (!term) return selectableSilos.value
  return selectableSilos.value.filter(
    (silo) => silo.label.toLowerCase().includes(term) || silo.value.toLowerCase().includes(term),
  )
})

const focusableSelectors =
  'input:not([disabled]):not([tabindex="-1"]), textarea:not([disabled]):not([tabindex="-1"]), button:not([disabled]):not([tabindex="-1"]), [role="menuitem"], [role="menuitemcheckbox"], [href]:not([aria-disabled="true"])'

function getFocusableElements() {
  if (!contentRef.value) return []

  return Array.from(contentRef.value.querySelectorAll<HTMLElement>(focusableSelectors)).filter(
    (element) => {
      if (element.dataset.ignoreTab === 'true') return false
      if (element.getAttribute('aria-hidden') === 'true') return false
      if (element.hasAttribute('disabled') || element.getAttribute('aria-disabled') === 'true') {
        return false
      }
      return element.getClientRects().length > 0
    },
  )
}

function handleContentKeydown(event: KeyboardEvent) {
  if (event.key !== 'Tab') return

  const focusableElements = getFocusableElements()
  if (focusableElements.length === 0) return

  const activeElement = document.activeElement as HTMLElement | null
  const currentIndex = activeElement ? focusableElements.indexOf(activeElement) : -1

  // Move focus to the first/last element when Tab starts from outside the list
  if (currentIndex === -1) {
    event.preventDefault()
    event.stopPropagation()
    const targetIndex = event.shiftKey ? focusableElements.length - 1 : 0
    focusableElements[targetIndex]?.focus()
    return
  }

  const isAtStart = currentIndex === 0
  const isAtEnd = currentIndex === focusableElements.length - 1

  // Allow Tab to escape the menu when focus is at either boundary
  if ((!event.shiftKey && isAtEnd) || (event.shiftKey && isAtStart)) {
    return
  }

  event.preventDefault()
  event.stopPropagation()

  const nextIndex = event.shiftKey ? currentIndex - 1 : currentIndex + 1
  focusableElements[nextIndex]?.focus()
}

// Count of selected silos
const selectedCount = computed(() => localSelectedSilos.value.size)

// Display text for button
const buttonText = computed(() => {
  if (selectedCount.value === 1) {
    const selectedSilo = Array.from(localSelectedSilos.value)[0]
    const silo = availableSilos.value.find((s) => s.value === selectedSilo)
    return silo?.label || selectedSilo
  } else if (selectedCount.value > 1) {
    const firstSilo = availableSilos.value.find(
      (s) => s.value === Array.from(localSelectedSilos.value)[0],
    )
    return `${firstSilo?.label || (props.applicationsOnly ? 'Application' : 'Silo')} +${selectedCount.value - 1}`
  }
  return props.applicationsOnly ? 'Select Applications' : 'Select Silos'
})

// Selected silos as chips (value + label) for the sticky header
const selectedSiloChips = computed(() =>
  Array.from(localSelectedSilos.value)
    .map((id) => ({
      value: id,
      label: availableSilos.value.find((s) => s.value === id)?.label || id,
    }))
    .sort((a, b) => a.label.localeCompare(b.label)),
)

// Tooltip text showing all selected silos
const tooltipText = computed(() => {
  if (selectedCount.value === 0) return 'No silos selected'

  const siloNames = Array.from(localSelectedSilos.value)
    .map((id) => availableSilos.value.find((s) => s.value === id)?.label || id)
    .join(', ')

  return `Selected: ${siloNames}`
})

function toggleSilo(siloValue: string) {
  if (localSelectedSilos.value.has(siloValue)) {
    // Prevent clearing the last remaining silo
    if (localSelectedSilos.value.size === 1) return
    localSelectedSilos.value = new Set([...localSelectedSilos.value].filter((v) => v !== siloValue))
  } else {
    localSelectedSilos.value = new Set([...localSelectedSilos.value, siloValue])
  }
  emit('updateSilos', Array.from(localSelectedSilos.value))
}

function selectOnlySilo(siloValue: string) {
  localSelectedSilos.value = new Set([siloValue])
  emit('updateSilos', Array.from(localSelectedSilos.value))
}

function removeSilo(siloValue: string) {
  if (localSelectedSilos.value.size === 1) return
  localSelectedSilos.value = new Set([...localSelectedSilos.value].filter((v) => v !== siloValue))
  emit('updateSilos', Array.from(localSelectedSilos.value))
}

function selectAllSilos() {
  if (!canSelectAll.value) return
  localSelectedSilos.value = new Set(selectableSilos.value.map((s) => s.value))
  emit('updateSilos', Array.from(localSelectedSilos.value))
}

function resetToDefaultSilo() {
  if (props.resetToClear) {
    localSelectedSilos.value = new Set()
    emit('updateSilos', [])
    return
  }

  // Keep at least one silo selected (first available)
  if (selectableSilos.value.length > 0) {
    localSelectedSilos.value = new Set([selectableSilos.value[0].value])
    emit('updateSilos', Array.from(localSelectedSilos.value))
  }
}

// Manage open state; no reset on close since selection applies immediately
function handleOpenChange(open: boolean) {
  dropdownOpen.value = open
  if (open) {
    setFieldValue('siloSearch', '')
  }
}

const contentClass = computed(
  () =>
    `w-[20rem] ${props.contentZClass ?? 'z-40'} bg-white rounded-lg border border-gray-300 shadow-[0_10px_40px_10px_rgba(0,0,0,0.08)] overflow-hidden will-change-[transform,opacity] data-[side=bottom]:animate-slideDownAndFade data-[side=left]:animate-slideLeftAndFade data-[side=right]:animate-slideRightAndFade data-[side=top]:animate-slideUpAndFade data-[state=closed]:animate-hide dark:bg-gray-900 dark:border-gray-700 dark:shadow-[0_10px_40px_10px_rgba(0,0,0,0.2)]`,
)

const itemClass =
  'group flex w-full items-center gap-x-2 rounded-lg border border-transparent cursor-pointer px-2 py-1.5 focus:outline-none text-sm text-gray-900 transition-colors data-[highlighted]:bg-gray-50 hover:bg-gray-50 disabled:opacity-50 disabled:pointer-events-none dark:border-gray-700 dark:text-gray-100 dark:data-[highlighted]:bg-gray-800/70 dark:hover:bg-gray-800/70'
</script>

<template>
  <div class="relative">
    <DropdownMenuRoot v-model:open="dropdownOpen" :modal="false" @update:open="handleOpenChange">
      <DropdownMenuTrigger as-child>
        <Button
          type="secondary"
          size="md"
          dropdown
          class="min-w-[112px] max-w-[12rem] sm:max-w-[16rem] h-[32px] overflow-hidden !px-2.5 gap-x-1.5"
          :title="tooltipText"
        >
          <Icon variant="filter" class="size-4 shrink-0" />
          <span class="min-w-0 truncate">{{ buttonText }}</span>
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuPortal
        :to="portalTarget ?? '#app-root'"
        :disabled="portalDisabled"
      >
        <DropdownMenuContent
          :class="contentClass"
          align="end"
          side="bottom"
          :side-offset="8"
          :collision-padding="10"
        >
          <div ref="contentRef" class="flex flex-col max-h-[28rem]" @keydown="handleContentKeydown">
            <!-- Sticky Header with Search & Bulk Actions -->
            <div
              class="sticky top-0 z-10 rounded-t-lg border-b border-gray-200 backdrop-blur-sm bg-white/95 dark:border-gray-700 dark:bg-gray-900/95"
            >
              <div class="flex justify-between items-center px-3 pt-2.5 pb-1.5">
                <h3
                  class="flex gap-x-2 items-center text-sm font-semibold text-gray-900 dark:text-gray-100"
                >
                  <Icon variant="layers" class="size-4" />
                  {{ applicationsOnly ? 'Filter by Application' : 'Filter by Silo' }}
                </h3>
                <span class="text-xs font-medium text-gray-500 dark:text-gray-400">
                  {{ selectedCount }} of {{ selectableSilos.length }} selected
                </span>
              </div>

              <div class="px-3 pb-2 space-y-2">
                <!-- Search Input -->
                <div class="relative">
                  <FieldText
                    variant="gray"
                    type="text"
                    :show-clear="true"
                    name="siloSearch"
                    :show-label="false"
                    :validate="false"
                    prefix-icon="search"
                    color="default"
                    :placeholder="applicationsOnly ? 'Search applications...' : 'Search silos...'"
                  />
                </div>

                <!-- Bulk Action Buttons -->
                <div class="flex gap-x-1.5">
                  <Button
                    v-if="canSelectAll"
                    type="secondary"
                    size="sm"
                    class="flex-1 justify-center items-center text-sm font-medium"
                    @click="selectAllSilos"
                  >
                    Select All
                  </Button>
                  <Button
                    type="secondary"
                    size="sm"
                    class="flex-1 justify-center items-center text-sm font-medium text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-gray-100"
                    @click="resetToDefaultSilo"
                  >
                    {{ resetToClear ? 'Clear' : 'Reset Defaults' }}
                  </Button>
                </div>

                <!-- Selected Chips (removable without scrolling) -->
                <div
                  v-if="selectedSiloChips.length > 0"
                  class="flex gap-x-1.5 gap-y-1.5 flex-wrap max-h-20 overflow-y-auto overflow-x-hidden"
                >
                  <div
                    v-for="chip in selectedSiloChips"
                    :key="chip.value"
                    class="inline-flex items-center gap-x-0.5 rounded border border-gray-300 bg-gray-50 px-1 py-px text-xs font-medium text-gray-900 dark:border-gray-700 dark:bg-gray-700/10 dark:text-white"
                  >
                    <span class="inline-block max-w-[8rem] whitespace-normal break-words leading-4">
                      {{ chip.label }}
                    </span>
                    <button
                      type="button"
                      tabindex="-1"
                      class="inline-flex shrink-0 p-0.5 size-3.5 items-center justify-center text-gray-400 rounded-full hover:bg-gray-200 hover:text-gray-500 transition-colors focus:outline-none disabled:cursor-not-allowed disabled:opacity-40"
                      :aria-label="`Remove ${chip.label}`"
                      :disabled="selectedSiloChips.length === 1"
                      @click.stop="removeSilo(chip.value)"
                    >
                      <span class="sr-only">Remove {{ chip.label }}</span>
                      <svg class="size-2" stroke="currentColor" fill="none" viewBox="0 0 8 8">
                        <path stroke-linecap="round" stroke-width="1.5" d="M1 1l6 6m0-6L1 7" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Loading State -->
            <div v-if="silosLoading" class="flex justify-center items-center p-6">
              <Loading />
            </div>

            <!-- Scrollable List Container -->
            <div v-else ref="scrollContainerRef" class="overflow-y-auto flex-1 p-1">
              <template v-if="filteredSilos.length > 0">
                <DropdownMenuItem
                  v-for="(silo, index) in filteredSilos"
                  :key="silo.value"
                  @select.prevent="toggleSilo(silo.value)"
                  :class="[
                    itemClass,
                    localSelectedSilos.has(silo.value)
                      ? 'border-primary-200 bg-primary-50 text-primary-900 dark:border-primary-600/40 dark:bg-primary-900/30 dark:text-primary-100'
                      : 'border-transparent',
                    index > 0 ? 'mt-0.5' : '',
                  ]"
                >
                  <div class="flex gap-x-2 items-center w-full">
                    <Checkbox
                      :model-value="localSelectedSilos.has(silo.value)"
                      :aria-label="`Toggle ${silo.label}`"
                      class="shrink-0"
                      tabindex="-1"
                      data-ignore-tab="true"
                      @update:modelValue="() => toggleSilo(silo.value)"
                      @click.stop
                    />
                    <div
                      class="flex flex-1 gap-x-1.5 items-center min-w-0 cursor-pointer"
                      @click.stop="selectOnlySilo(silo.value)"
                    >
                      <Icon
                        v-if="silo.icon"
                        :variant="silo.icon"
                        class="shrink-0 text-gray-500 transition-colors size-4 group-hover:text-gray-700 dark:text-gray-400 dark:group-hover:text-gray-200"
                      />
                      <span
                        class="min-w-0 flex-1 whitespace-normal break-words font-medium leading-5 group-hover:underline"
                      >
                        {{ silo.label }}
                      </span>
                    </div>
                  </div>
                </DropdownMenuItem>
              </template>

              <!-- No Results Message -->
              <div v-else class="flex flex-col justify-center items-center px-4 py-6 text-center">
                <Icon variant="search-x" class="mb-2 text-gray-400 size-8 dark:text-gray-600" />
                <p class="text-sm font-medium text-gray-900 dark:text-gray-100">
                  No {{ applicationsOnly ? 'applications' : 'silos' }} found
                </p>
                <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Try adjusting your search
                </p>
              </div>
            </div>

            <!-- Done Button -->
            <div
              v-if="availableSilos.length > 0"
              class="flex justify-end px-2 py-1.5 border-t border-gray-200 dark:border-neutral-700"
            >
              <Button class="w-full" size="sm" @click="dropdownOpen = false">Done</Button>
            </div>
          </div>
        </DropdownMenuContent>
      </DropdownMenuPortal>
    </DropdownMenuRoot>
  </div>
</template>
